/**
 * Modern Landing Page
 * Contemporary UX design with mobile-first approach, modern animations, and better conversion flow
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  QrCodeIcon,
  PrinterIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  BoltIcon,
  GlobeAltIcon,
  StarIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ClockIcon,
  HeartIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const useInView = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView] as const;
};

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({
  end,
  duration = 2000,
  suffix = '',
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};



const ModernLandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Immediate redirect if shop parameter is present (from QR scan)
  useEffect(() => {
    const shopId = searchParams.get('shop');
    if (shopId) {
      console.log('[ModernLandingPage] Shop ID detected, redirecting to /send');
      navigate(`/send?shop=${shopId}`, { replace: true });
    }
  }, [searchParams, navigate]);
  const [scrolled, setScrolled] = useState(false);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowFloatingCta(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [heroRef, heroInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const features = [
    {
      icon: QrCodeIcon,
      title: 'Instant QR Scanning',
      description: 'Simply scan the shop QR code and start uploading your documents instantly.',
      color: 'bg-blue-500',
    },
    {
      icon: PrinterIcon,
      title: 'Real-time Processing',
      description: 'Shop owners receive documents immediately with Gmail-like inbox experience.',
      color: 'bg-green-500',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy First',
      description: 'Documents auto-delete after confirmation for complete privacy protection.',
      color: 'bg-purple-500',
    },
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Upload, print, and confirm in minutes with our optimized workflow.',
      color: 'bg-yellow-500',
    },
  ];

  const stats = [
    { label: 'Documents Printed', value: 50000, suffix: '+' },
    { label: 'Happy Customers', value: 5000, suffix: '+' },
    { label: 'Partner Shops', value: 200, suffix: '+' },
    { label: 'Cities', value: 25, suffix: '+' },
  ];

  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "How do I find a Prindox enabled shop?",
      answer: "Look for the Prindox QR code sticker at local print shops or use our 'Find Shops' feature to locate the nearest partner shop in your area."
    },
    {
      question: "Is it safe to upload my private documents?",
      answer: "Absolutely. We use end-to-end encryption for transfers. Most importantly, documents are automatically deleted from the shop's system immediately after you confirm the print or after 24 hours."
    },
    {
      question: "What file formats are supported?",
      answer: "We support PDF, DOCX, and all major image formats (JPG, PNG). PDF is recommended for the best printing results."
    },
    {
      question: "How do I pay for the printing?",
      answer: "Currently, you pay the shop owner directly via cash or their preferred UPI method, just like usual. Prindox makes the document transfer seamless."
    }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'Student',
      content: 'Prindox saved my exam prep! No more running around looking for print shops.',
      avatar: '👩‍🎓',
      rating: 5,
    },
    {
      name: 'Raj Printing',
      role: 'Shop Owner',
      content: 'Our business increased 40% with Prindox. Customers love the convenience.',
      avatar: '👨‍💼',
      rating: 5,
    },
    {
      name: 'Amit Kumar',
      role: 'Professional',
      content: 'Perfect for urgent document printing. Clean, fast, and reliable every time.',
      avatar: '👨‍💻',
      rating: 5,
    },
  ];

  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 pt-safe-top ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-1'
          : 'bg-transparent py-2'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/logo-icon.svg" alt="Prindox Logo" className="w-10 h-10 mr-2 object-contain" />
              <span className="text-xl font-black tracking-[-0.05em] text-gray-900">
                PRINDOX
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </a>
              {!isAuthenticated ? (
                <>
                  <Link to="/register?type=shopkeeper" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                    Partner with Us
                  </Link>
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="bg-blue-600 text-white px-6 py-2 rounded-full font-bold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    Get Started
                  </Link>
                </>
              ) : (
                <Link
                  to={user?.is_shopkeeper ? "/dashboard" : "/send"}
                  className="bg-blue-600 text-white px-6 py-2 rounded-full font-bold hover:bg-blue-700 transition-all"
                >
                  {user?.is_shopkeeper ? "Go to Dashboard" : "Start Scanning"}
                </Link>
              )}
            </div>

            {/* Mobile Action Button (Instead of hamburger) */}
            <div className="md:hidden">
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="text-[10px] font-black uppercase tracking-widest text-white bg-blue-600 px-5 py-2.5 rounded-full shadow-lg shadow-blue-200"
                >
                  Sign In
                </Link>
              ) : (
                <Link
                  to={user?.is_shopkeeper ? "/dashboard" : "/send"}
                  className="text-[10px] font-black uppercase tracking-widest text-white bg-blue-600 px-5 py-2.5 rounded-full shadow-lg shadow-blue-200"
                >
                  {user?.is_shopkeeper ? "Inbox" : "Scan"}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto">
          <div
            ref={heroRef}
            className={`text-center transition-all duration-1000 ${
              heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex flex-wrap justify-center gap-4 mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-full text-blue-700 text-sm font-semibold shadow-sm">
                <SparklesIcon className="w-4 h-4 mr-2 animate-pulse" />
                India's #1 Instant Printing Network
              </div>
              <a href="#/download" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white border border-blue-500 rounded-full text-sm font-semibold shadow-md animate-bounce-slow hover:bg-blue-700 transition-colors">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L18.65,16.18C19.18,16.49 19.18,17.26 18.65,17.57L15.4,19.43L12.42,16.42L16.81,15.12M4.56,1.73L15.4,7.92L12.42,10.91L4.56,1.73M15.4,16.08L4.56,22.27L12.42,13.09L15.4,16.08Z" />
                </svg>
                Now available on Android & iOS
              </a>            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 mb-8 leading-[1.1] tracking-tight">
              Print Anything
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Everywhere.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
              Join the revolution of instant document sharing. Scan, Upload, and Print at any Prindox shop in under 60 seconds.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Link
                to="/send"
                className="w-full sm:w-auto px-12 py-6 bg-slate-900 text-white rounded-[2rem] text-xl font-black uppercase tracking-[0.2em] shadow-[0_20px_50px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_50px_rgba(59,130,246,0.3)] hover:bg-blue-600 transition-all duration-500 transform hover:-translate-y-1 flex items-center justify-center group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  Start Printing
                  <QrCodeIcon className="w-6 h-6 ml-3 group-hover:rotate-12 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Link>

              <Link
                to="/find-shops"
                className="w-full sm:w-auto px-8 py-4 text-slate-600 font-bold hover:text-slate-900 transition-colors flex items-center justify-center space-x-2"
              >
                <GlobeAltIcon className="w-5 h-5" />
                <span>Nearby Shops</span>
              </Link>
            </div>


          </div>
        </div>
      </section>

      {/* Floating Mobile CTA */}
      <div className={`fixed bottom-8 left-4 right-4 z-50 md:hidden transition-all duration-500 transform ${showFloatingCta ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
        <Link
          to="/send"
          className="flex items-center justify-center w-full bg-gray-900 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-2xl border border-white/10 backdrop-blur-lg"
        >
          <QrCodeIcon className="w-6 h-6 mr-2" />
          Scan & Print Now
        </Link>
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            ref={featuresRef}
            className={`text-center mb-16 transition-all duration-1000 delay-200 ${
              featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Prindox?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the future of document printing with our innovative platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`group p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-gray-200 ${
                  featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100 + 400}ms` }}
              >
                <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get your documents printed in just 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Scan QR Code',
                description: 'Find a nearby print shop and scan their QR code with your phone camera',
                icon: QrCodeIcon,
                color: 'from-blue-500 to-cyan-500',
              },
              {
                step: '02',
                title: 'Upload Documents',
                description: 'Select and upload your documents with print preferences and special instructions',
                icon: DocumentTextIcon,
                color: 'from-purple-500 to-pink-500',
              },
              {
                step: '03',
                title: 'Print & Collect',
                description: 'Shop owner prints your documents instantly. Collect and confirm receipt',
                icon: CheckCircleIcon,
                color: 'from-green-500 to-emerald-500',
              },
            ].map((step, index) => (
              <div key={step.step} className="relative">
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 right-0 w-full h-0.5 bg-gradient-to-r from-gray-300 to-transparent transform translate-x-8"></div>
                )}
                <div className="text-center">
                  <div className={`w-24 h-24 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-sm font-bold text-gray-400 mb-2">STEP {step.step}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed max-w-sm mx-auto">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Demo CTA */}
          <div className="text-center mt-16">
            <Link
              to="/send"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Try It Now - It's Free
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Security & Trust Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-blue-100 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-1/2">
              <div className="inline-flex items-center px-3 py-1 bg-green-100 rounded-full text-green-700 text-sm font-medium mb-4">
                <ShieldCheckIcon className="w-4 h-4 mr-2" />
                Bank-Grade Security
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Privacy is Our Priority</h2>
              <p className="text-gray-600 text-lg mb-6">
                We believe your documents are your business. Our automated cleanup system ensures 
                that once your job is done, your files are wiped from the shop's dashboard forever.
              </p>
              <ul className="space-y-3">
                {[
                  "256-bit SSL Encryption",
                  "Automated Document Cleanup",
                  "No Permanent Storage of Personal Files",
                  "ULID-based Secure Shop Identification"
                ].map((item, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-1/2 grid grid-cols-2 gap-4">
              <div className="p-6 bg-blue-50 rounded-2xl text-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900">0 min</div>
                <div className="text-sm text-gray-500">Manual Wait Time</div>
              </div>
              <div className="p-6 bg-purple-50 rounded-2xl text-center">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheckIcon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-500">Auto-Deletion</div>
              </div>
              <div className="p-6 bg-green-50 rounded-2xl text-center col-span-2">
                <div className="flex items-center justify-center space-x-4">
                  <GlobeAltIcon className="w-8 h-8 text-green-600" />
                  <div className="text-left">
                    <div className="text-xl font-bold text-gray-900">End-to-End Secure</div>
                    <div className="text-sm text-gray-500">Encrypted document transfers across all nodes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="max-w-7xl mx-auto">
          <div
            ref={statsRef}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white"
          >
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`transition-all duration-1000 ${
                  statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-blue-200 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Shop Owners Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 uppercase tracking-widest mb-4">
                For Business
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6 leading-tight">
                Grow Your Print Business with Prindox
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Stop dealing with USB drives, Bluetooth pairing failures, and email attachments. 
                Join our network to receive documents securely and instantly from customers' phones directly to your dashboard.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  "Zero hardware cost - works on any browser",
                  "Receive files instantly via secure cloud",
                  "Automated privacy & file cleanup",
                  "Dashboard to manage orders efficiently"
                ].map((item, i) => (
                  <li key={i} className="flex items-center text-slate-700 font-medium">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link
                to="/register?type=shopkeeper"
                className="inline-flex items-center bg-slate-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <BuildingStorefrontIcon className="w-5 h-5 mr-2" />
                Register Your Shop
              </Link>
            </div>
            
            <div className="order-1 lg:order-2 relative">
              <div className="absolute inset-0 bg-blue-600 rounded-[2rem] rotate-3 opacity-10"></div>
              <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-slate-100 relative overflow-hidden">
                <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BuildingStorefrontIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">Shop Dashboard</div>
                      <div className="text-xs text-green-600 font-bold flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                        Live
                      </div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-slate-400">PRINDOX PARTNER</div>
                </div>
                
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-lg mr-4">
                        📄
                      </div>
                      <div className="flex-1">
                        <div className="h-2 w-24 bg-slate-200 rounded mb-2"></div>
                        <div className="h-2 w-16 bg-slate-100 rounded"></div>
                      </div>
                      <button className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-lg">
                        PRINT
                      </button>
                    </div>
                  ))}
                  
                  <div className="flex items-center p-4 bg-blue-50/50 rounded-xl border border-blue-100 relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-lg mr-4 animate-bounce">
                      🔔
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-900">New Order Received</div>
                      <div className="text-xs text-slate-500">Just now • 3 Files</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Loved by Thousands
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what our customers and shop partners say about us
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="text-3xl mr-4">{testimonial.avatar}</div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Prindox
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300 hover:border-blue-300 shadow-sm"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <span className={`transform transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}>
                    <ArrowRightIcon className="w-5 h-5 text-gray-400 rotate-90" />
                  </span>
                </button>
                {activeFaq === index && (
                  <div className="px-6 pb-6 text-gray-600 animate-fadeIn">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section id="download" className="py-20 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl overflow-hidden shadow-2xl relative">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center">
              <div className="p-8 md:p-16 md:w-3/5 text-center md:text-left">
                <span className="inline-block px-4 py-1.5 bg-blue-500/30 text-white text-xs font-bold rounded-full uppercase tracking-widest mb-6">
                  Experience Prindox Anywhere
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  Download the App
                </h2>
                <p className="text-xl text-blue-100 mb-10 max-w-lg">
                  Scan codes faster, track prints in real-time, and manage your shop from anywhere with the native Prindox app.
                </p>

                <div className="flex flex-wrap justify-center md:justify-start gap-4">
                  {/* Google Play Store Badge */}
                  <a href="#" className="transform transition-all hover:scale-105 active:scale-95">
                    <div className="bg-black text-white px-5 py-2.5 rounded-xl border border-gray-800 flex items-center shadow-lg hover:bg-gray-900 transition-colors">
                      <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24">
                        <path fill="#00A1F1" d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5Z" />
                        <path fill="#7DB700" d="M16.81,15.12L18.65,16.18C19.18,16.49 19.18,17.26 18.65,17.57L15.4,19.43L12.42,16.42L16.81,15.12Z" />
                        <path fill="#FDB900" d="M4.56,1.73L15.4,7.92L12.42,10.91L4.56,1.73Z" />
                        <path fill="#ED3B00" d="M15.4,16.08L4.56,22.27L12.42,13.09L15.4,16.08Z" />
                      </svg>
                      <div className="text-left">
                        <div className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-0.5">Get it on</div>
                        <div className="text-lg font-bold leading-tight tracking-tight">Google Play</div>
                      </div>
                    </div>
                  </a>

                  {/* App Store Badge */}
                  <a href="#" className="transform transition-all hover:scale-105 active:scale-95">
                    <div className="bg-black text-white px-5 py-2.5 rounded-xl border border-gray-800 flex items-center shadow-lg hover:bg-gray-900 transition-colors">
                      <svg className="w-7 h-7 mr-3" fill="currentColor" viewBox="0 0 384 512">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                      </svg>
                      <div className="text-left">
                        <div className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-0.5">Download on the</div>
                        <div className="text-lg font-bold leading-tight tracking-tight">App Store</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Floating Phone Illustration */}
              <div className="md:w-2/5 p-8 flex justify-center items-end self-end">
                <div className="relative w-64 h-[450px] bg-slate-900 rounded-[3rem] border-[8px] border-slate-800 shadow-2xl transform rotate-6 translate-y-12">
                  {/* Phone Speaker */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-16 h-1.5 bg-slate-800 rounded-full"></div>
                  {/* Internal Screen Content */}
                  <div className="mt-8 mx-2 h-[380px] bg-white rounded-[2rem] overflow-hidden p-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full mb-4 mx-auto flex items-center justify-center text-white text-xs font-bold animate-pulse">P</div>
                    <div className="space-y-3">
                      <div className="h-4 w-3/4 bg-slate-100 rounded mx-auto"></div>
                      <div className="h-2 w-1/2 bg-slate-50 rounded mx-auto"></div>
                      <div className="h-32 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center">
                        <QrCodeIcon className="w-12 h-12 text-slate-300" />
                      </div>
                      <div className="h-10 w-full bg-blue-600 rounded-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-blue-900">
        <div className="max-w-4xl mx-auto text-center">
          <div
            ref={ctaRef}
            className={`transition-all duration-1000 ${
              ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Printing Experience?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers and shop owners who've made printing effortless
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center"
              >
                <UserGroupIcon className="w-5 h-5 mr-2" />
                Start as User
              </Link>

              <Link
                to="/register?type=shopkeeper"
                className="bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center"
              >
                <BuildingStorefrontIcon className="w-5 h-5 mr-2" />
                Register Your Shop
              </Link>
            </div>

            <p className="text-sm text-gray-400 mt-6">
              No setup fees • Free to start • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* Custom Styles for Blob Animation */}
      <style dangerouslySetInnerHTML={{ __html: `
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
      `}} />
    </div>
  );
};

export default ModernLandingPage;